import React, { useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const base = `/${lang}`;

  const scrollToAnchor = (id) => (e) => {
    if (location.pathname === base || location.pathname === base + "/") {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  const navLinks = [
    { to: `${base}#how`, id: "how", label: t("nav.how") },
    { to: `${base}#projects`, id: "projects", label: t("nav.projects") },
    { to: `${base}#about`, id: "about", label: t("nav.about") },
    { to: `${base}#contact`, id: "contact", label: t("nav.contact") },
  ];

  return (
    <>
      <a href="#main" className="zx-skip">{t("nav.skip")}</a>
      <header className={`zx-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="zx-header__inner">
          <Link to={base} className="zx-header__brand" aria-label="Zyravex">
            <Logo />
          </Link>

          <nav className="zx-header__nav" aria-label="Primary">
            {navLinks.map((l) => (
              <a key={l.id} href={l.to} onClick={scrollToAnchor(l.id)} className="zx-header__link">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="zx-header__right">
            <LanguageSwitcher />
            <a
              href={`${base}#contact`}
              onClick={scrollToAnchor("contact")}
              className="zx-btn zx-btn--primary zx-btn--sm zx-header__cta"
            >
              {t("hero.ctaPrimary")}
            </a>
            <button
              type="button"
              className="zx-hamburger"
              aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <div className={`zx-mobile-menu ${open ? "is-open" : ""}`}>
        <nav className="zx-mobile-menu__nav" aria-label="Mobile">
          <Link to={base} className="zx-mobile-menu__link" onClick={() => setOpen(false)}>{t("nav.home")}</Link>
          {navLinks.map((l) => (
            <a key={l.id} href={l.to} onClick={scrollToAnchor(l.id)} className="zx-mobile-menu__link">
              {l.label}
            </a>
          ))}
          <div className="zx-mobile-menu__foot">
            <LanguageSwitcher />
          </div>
          <a
            href={`${base}#contact`}
            onClick={scrollToAnchor("contact")}
            className="zx-btn zx-btn--primary"
          >
            {t("hero.ctaPrimary")}
          </a>
        </nav>
      </div>
    </>
  );
}
